import React from 'react';

import {
  AuthRoute,
  CommonRoute,
  FollowUsersRoute,
  GroupRoute,
  OnboardedStackRoute,
  OnboardedTabRoute,
  OnboardingRoute,
} from '@navigation/Routes';
import type {
  RootNavigationParamList,
  ViewFollowsScreenToggleState,
  ViewPlanScreenToggleState,
} from '@navigation/Types';
import { NavigationProp, useNavigation } from '@react-navigation/native';

/** Optional view state opened inside the plan detail screen. */
export type OpenPlanOptions = {
  reload?: boolean;
  toggleState?: ViewPlanScreenToggleState;
};

/** Optional view state opened inside the follows screen. */
export type OpenFollowsOptions = {
  toggleState?: ViewFollowsScreenToggleState;
};

type AppNavigationSource = Pick<NavigationProp<RootNavigationParamList>, 'dispatch' | 'navigate'>;

export function createAppNavigation(navigation: AppNavigationSource) {
  return {
    openChat: (chatId: string) => navigation.navigate(CommonRoute.Chat, { chatId }),
    openChatThreads: () => navigation.navigate(CommonRoute.ChatThreads),
    openCreateChat: () => navigation.navigate(CommonRoute.CreateChat),
    openCreateGroup: () => navigation.navigate(CommonRoute.CreateGroup),
    openCreatePlan: () => navigation.navigate(OnboardedStackRoute.CreatePlan),
    openCurrentUserProfile: () => navigation.navigate(CommonRoute.CurrentUserProfile),
    openEditCurrentUser: () => navigation.navigate(CommonRoute.UpdateUser),
    openEditGroup: (groupId: string) => navigation.navigate(CommonRoute.UpdateGroup, { groupId }),
    openEditPlan: (planId: string) => navigation.navigate(CommonRoute.UpdatePlan, { planId }),
    openGroup: (groupId: string, options: { reload?: boolean } = {}) =>
      navigation.navigate(CommonRoute.ViewGroup, { groupId, ...options }),
    openGroupMembers: (groupId: string) =>
      navigation.navigate(CommonRoute.ViewGroupMembers, { groupId }),
    openGroupsTab: () =>
      navigation.navigate(OnboardedStackRoute.OnboardedTab, {
        screen: OnboardedTabRoute.Groups,
        params: { screen: GroupRoute.ViewGroups, params: { screenType: 'tab' } },
      }),
    openInviteContacts: () => navigation.navigate(CommonRoute.InviteContacts),
    openLogin: () => navigation.navigate(AuthRoute.Login),
    openNotifications: () => navigation.navigate(CommonRoute.Notifications),
    openOnboardingGroups: () =>
      navigation.navigate(OnboardingRoute.JoinGroups, { screenType: 'onboarding' }),
    openOnboardingPeople: () =>
      navigation.navigate(OnboardingRoute.FollowUsers, { screenType: 'onboarding' }),
    openOnboardingPermissions: () => navigation.navigate(OnboardingRoute.RequestPermissions),
    openPeopleTab: () =>
      navigation.navigate(OnboardedStackRoute.OnboardedTab, {
        screen: OnboardedTabRoute.FollowUsers,
        params: { screen: FollowUsersRoute.FollowUsers, params: { screenType: 'tab' } },
      }),
    openPlan: (planId: string, options: OpenPlanOptions = {}) =>
      navigation.navigate(CommonRoute.ViewPlan, { planId, ...options }),
    openProfile: (userId: string) => navigation.navigate(CommonRoute.Profile, { userId }),
    openRegister: () => navigation.navigate(AuthRoute.Register),
    openResetPassword: () => navigation.navigate(AuthRoute.ResetPassword),
    openSettings: () => navigation.navigate(CommonRoute.Settings),
    openUserFollows: (userId: string, userDisplayName: string, options: OpenFollowsOptions = {}) =>
      navigation.navigate(CommonRoute.ViewFollows, {
        userId,
        userDisplayName,
        ...options,
      }),
    openUserPrivateForm: () => navigation.navigate(OnboardingRoute.UserPrivateForm),
    openWelcome: () => navigation.navigate(OnboardingRoute.Welcome),
  };
}

export type AppNavigation = ReturnType<typeof createAppNavigation>;

export function useAppNavigation() {
  const navigation = useNavigation<NavigationProp<RootNavigationParamList>>();
  return React.useMemo(() => createAppNavigation(navigation), [navigation]);
}
